/**
 * Mini Music Player Component
 * Compact audio player for blog posts - displays a single track with play/pause and scrubber
 */

import { Play, Pause, Loader2, Volume2, VolumeX } from 'lucide-react';
import { useAudio } from '~/contexts/AudioContext';
import { getTrack } from '~/data/music-tracks';

interface MiniMusicPlayerProps {
  trackId: string;
}

export function MiniMusicPlayer({ trackId }: MiniMusicPlayerProps) {
  const { currentTrackId, isPlaying, currentTime, duration, isLoading, volume, isMuted, togglePlay, seek, setVolume, toggleMute } =
    useAudio();

  const track = getTrack(trackId);

  if (!track) {
    return (
      <div className="my-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
        Error: Track "{trackId}" not found
      </div>
    );
  }

  const isThisTrackPlaying = currentTrackId === trackId && isPlaying;
  const isThisTrackActive = currentTrackId === trackId;
  const progress = isThisTrackActive && duration > 0 ? (currentTime / duration) * 100 : 0;

  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    seek(newTime);
  };

  const handlePlayPause = () => {
    togglePlay(trackId, track);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value) / 100;
    setVolume(newVolume);
  };

  const volumePercentage = volume * 100;

  return (
    <div className="my-6 not-prose">
      <div
        className={`
          relative overflow-hidden rounded-lg border transition-all duration-300
          ${
            isThisTrackPlaying
              ? 'bg-gradient-to-br from-purple-900/30 to-blue-900/30 border-purple-500/50 shadow-lg shadow-purple-500/20'
              : 'bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-500/30'
          }
        `}
      >
        {/* Animated background pulse when playing */}
        {isThisTrackPlaying && (
          <div className="absolute inset-0 animate-pulse-slow pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10" />
          </div>
        )}

        <div className="relative p-4 sm:p-5">
          {/* Track Title */}
          <div className="mb-3">
            <h4 className="text-base sm:text-lg font-semibold text-white">{track.title}</h4>
          </div>

          {/* Player Controls */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Play/Pause Button */}
            <button
              onClick={handlePlayPause}
              disabled={isLoading && isThisTrackActive}
              className="
                flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full
                bg-purple-600 hover:bg-purple-700 active:bg-purple-800
                text-white transition-all duration-200
                flex items-center justify-center
                shadow-md hover:shadow-lg hover:shadow-purple-500/30
                disabled:opacity-50 disabled:cursor-not-allowed
                focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-transparent
              "
              aria-label={isThisTrackPlaying ? 'Pause' : 'Play'}
            >
              {isLoading && isThisTrackActive ? (
                <Loader2 size={20} className="animate-spin" />
              ) : isThisTrackPlaying ? (
                <Pause size={20} />
              ) : (
                <Play size={20} className="ml-0.5" />
              )}
            </button>

            {/* Progress Bar and Time Display */}
            <div className="flex-1 min-w-0">
              {/* Progress Scrubber */}
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max={isThisTrackActive ? duration || 0 : 0}
                  value={isThisTrackActive ? currentTime : 0}
                  onChange={handleSeek}
                  disabled={!isThisTrackActive || duration === 0}
                  className="
                    w-full h-2 bg-transparent appearance-none cursor-pointer
                    disabled:cursor-not-allowed disabled:opacity-50
                    focus:outline-none
                  "
                  style={{
                    background: `linear-gradient(to right, #a855f7 0%, #a855f7 ${progress}%, #374151 ${progress}%, #374151 100%)`,
                  }}
                  aria-label="Seek through track"
                />
              </div>

              {/* Time Display */}
              <div className="flex justify-between items-center mt-1.5 text-xs sm:text-sm text-gray-400">
                <span className="tabular-nums">
                  {isThisTrackActive ? formatTime(currentTime) : '0:00'}
                </span>
                <span className="tabular-nums">
                  {isThisTrackActive && duration > 0 ? formatTime(duration) : '0:00'}
                </span>
              </div>
            </div>
          </div>

          {/* Volume Control */}
          <div className="flex items-center gap-2 sm:gap-3 mt-3 pt-3 border-t border-gray-700/30">
            {/* Volume Icon Button */}
            <button
              onClick={toggleMute}
              className="
                flex-shrink-0 text-gray-400 hover:text-white transition-colors duration-200
                focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-transparent
                rounded p-1
              "
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted || volume === 0 ? (
                <VolumeX size={18} className="sm:w-5 sm:h-5" />
              ) : (
                <Volume2 size={18} className="sm:w-5 sm:h-5" />
              )}
            </button>

            {/* Volume Slider */}
            <div className="flex-1 max-w-[120px]">
              <input
                type="range"
                min="0"
                max="100"
                value={isMuted ? 0 : volumePercentage}
                onChange={handleVolumeChange}
                className="
                  w-full h-2 bg-transparent appearance-none cursor-pointer
                  focus:outline-none
                "
                style={{
                  background: `linear-gradient(to right, #a855f7 0%, #a855f7 ${isMuted ? 0 : volumePercentage}%, #374151 ${isMuted ? 0 : volumePercentage}%, #374151 100%)`,
                }}
                aria-label="Volume control"
              />
            </div>

            {/* Volume Percentage */}
            <span className="text-xs text-gray-400 tabular-nums w-8 text-right">
              {Math.round(isMuted ? 0 : volumePercentage)}%
            </span>
          </div>
        </div>
      </div>

      <style>{`
        /* Custom range slider thumb styles */
        input[type='range']::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          background: #a855f7;
          cursor: pointer;
          border-radius: 50%;
          box-shadow: 0 0 6px rgba(168, 85, 247, 0.5);
          transition: all 0.2s ease;
        }

        input[type='range']::-webkit-slider-thumb:hover {
          width: 16px;
          height: 16px;
          box-shadow: 0 0 10px rgba(168, 85, 247, 0.7);
        }

        input[type='range']::-moz-range-thumb {
          width: 14px;
          height: 14px;
          background: #a855f7;
          cursor: pointer;
          border-radius: 50%;
          border: none;
          box-shadow: 0 0 6px rgba(168, 85, 247, 0.5);
          transition: all 0.2s ease;
        }

        input[type='range']::-moz-range-thumb:hover {
          width: 16px;
          height: 16px;
          box-shadow: 0 0 10px rgba(168, 85, 247, 0.7);
        }

        input[type='range']:disabled::-webkit-slider-thumb {
          cursor: not-allowed;
          opacity: 0.5;
        }

        input[type='range']:disabled::-moz-range-thumb {
          cursor: not-allowed;
          opacity: 0.5;
        }

        /* Pulse animation for playing state */
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .animate-pulse-slow {
          animation: pulse-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}
